/**
 * Plan & usage tab of the billing portal (FR-BILLING-01/02/05).
 *
 * Composes the tab and owns the two consequence-stating confirmations:
 *   1. current plan card — price, cadence, renewal / cancellation state;
 *   2. {@link UsagePanel} — usage vs. every hard limit;
 *   3. {@link PlanComparison} — the four tiers and their upgrade/downgrade CTAs;
 *   4. downgrade + cancellation dialogs, which spell out exactly what is lost.
 *
 * Every number rendered here comes from the service's `entitlements` block, so
 * a tenant on a custom price sheet sees ITS caps and price, not the catalog's.
 * Self-service plan changes are hidden for those tenants: a negotiated contract
 * is not a button.
 */
import { useMemo, useState } from 'react';

import { ConfirmDialog } from '../../components/confirm-dialog';
import { DeskSection } from '../../components/desk-section';
import type { BillingCadence, BillingPlan, BillingSubscriptionView } from '../../lib/api-types';

import {
  cadenceSuffix,
  formatDate,
  formatMoneyCents,
  limitLosses,
  planFeatureLosses,
} from './billing-format';
import { PlanComparison } from './plan-comparison';
import { UsagePanel } from './usage-panel';

/** Free-tier caps quoted in the cancellation consequence copy. */
const FREE_TIER_SEATS = 2;
const FREE_TIER_GAMES = 5;

export interface PlanSelectorProps {
  view: BillingSubscriptionView;
  plans: readonly BillingPlan[];
  /** Paid upgrade → `POST /billing/v1/checkout-sessions` → hosted checkout. */
  onUpgrade: (plan: BillingPlan, cadence: BillingCadence) => void | Promise<void>;
  /**
   * Immediate plan change (downgrade) → `PATCH /subscriptions/{tenant_id}`.
   * Resolves `false` on failure, which keeps the confirm dialog open with the
   * message from `actionError` rather than losing the user's intent.
   */
  onChangePlan: (plan: BillingPlan) => Promise<boolean>;
  /** Cancel at period end. Same `false`-keeps-the-dialog-open contract. */
  onCancel: () => Promise<boolean>;
  /** Provider-hosted portal for payment-method changes. */
  onManagePayment?: () => void | Promise<void>;
  /** Last action failure, already human-readable. */
  actionError?: string | null;
  /** Re-runs the failed action, when it is safe to repeat (checkout, portal). */
  onRetryAction?: () => void;
  /** Limit key highlighted by a tier-limit CTA deep link (`?limit=seats`). */
  highlightLimitKey?: string | null;
}

export function PlanSelector({
  view,
  plans,
  onUpgrade,
  onChangePlan,
  onCancel,
  onManagePayment,
  actionError = null,
  onRetryAction,
  highlightLimitKey = null,
}: PlanSelectorProps): JSX.Element {
  const { entitlements, subscription, usage } = view;
  const [cadence, setCadence] = useState<BillingCadence>(
    entitlements.billing_cadence === 'annual' ? 'annual' : 'monthly'
  );
  const [pendingDowngrade, setPendingDowngrade] = useState<BillingPlan | null>(null);
  const [isCancelOpen, setIsCancelOpen] = useState(false);

  const ordered = useMemo(
    () => [...plans].sort((left, right) => left.price_monthly_cents - right.price_monthly_cents),
    [plans]
  );
  const currentIndex = ordered.findIndex((plan) => plan.plan_id === entitlements.plan_id);
  const nextPlan = currentIndex === -1 ? null : (ordered[currentIndex + 1] ?? null);
  const isCustomContract = entitlements.has_price_override;
  const isFreeTier = entitlements.price_cents === 0;
  // A trial has no card and auto-downgrades on day 30, so there is nothing to
  // cancel — the trial prompts own that decision instead.
  const isTrialing = entitlements.status === 'trialing';
  const isCancelling = subscription?.cancel_at_period_end === true;

  return (
    <div className="billing-plan">
      {actionError === null ? null : (
        <div className="banner banner--error" role="alert">
          <p>{actionError}</p>
          {onRetryAction === undefined ? null : (
            <div className="btn-row">
              <button type="button" className="btn btn--sm btn--secondary" onClick={onRetryAction}>
                Try again
              </button>
            </div>
          )}
        </div>
      )}

      <div className="desk-grid billing-desk-grid">
        <DeskSection
          title="Current plan"
          className="billing-card"
          data-testid="billing-current-plan"
          actions={
            isFreeTier || isTrialing || isCustomContract || isCancelling ? null : (
              <button
                type="button"
                className="btn btn--ghost btn--sm billing-quiet-danger"
                onClick={() => setIsCancelOpen(true)}
              >
                Cancel subscription
              </button>
            )
          }
        >
          <div className="billing-card__head">
            <p className="billing-card__plan">
              {entitlements.plan_display_name}
              {isCustomContract ? <span className="badge badge--accent">Custom</span> : null}
            </p>
            <p className="billing-card__price">
              <span className="billing-card__amount">
                {formatMoneyCents(entitlements.price_cents)}
              </span>
              <span className="muted">
                {entitlements.price_cents === 0 ? '' : cadenceSuffix(entitlements.billing_cadence)}
              </span>
            </p>
          </div>

          <dl className="billing-facts">
            <div>
              <dt>Status</dt>
              <dd>
                <span className={`badge billing-status billing-status--${entitlements.status}`}>
                  {entitlements.status.replace(/_/gu, ' ')}
                </span>
              </dd>
            </div>
            <div>
              <dt>Billing cadence</dt>
              <dd>{entitlements.billing_cadence}</dd>
            </div>
            <div>
              <dt>{isCancelling ? 'Access ends' : 'Renews on'}</dt>
              <dd>{formatDate(subscription?.current_period_end)}</dd>
            </div>
          </dl>

          {isCancelling ? (
            <div className="banner banner--warning" role="status">
              <p>
                Cancellation scheduled. Your team keeps {entitlements.plan_display_name} access
                until {formatDate(subscription?.current_period_end)}, then moves to the Free tier.
                All game data is preserved.
              </p>
            </div>
          ) : null}

          {isCustomContract ? (
            <p className="muted billing-note">
              This plan is customised for your organisation
              {entitlements.overridden_fields.length === 0
                ? ''
                : ` (${entitlements.overridden_fields.join(', ')})`}
              . Contact your account manager to change it — self-service plan changes are disabled.
            </p>
          ) : null}
        </DeskSection>

        <UsagePanel
          usage={usage}
          currentPlanName={entitlements.plan_name}
          nextPlanName={isCustomContract ? null : (nextPlan?.name ?? null)}
          highlightLimitKey={highlightLimitKey}
          onUpgrade={nextPlan === null ? undefined : () => void onUpgrade(nextPlan, cadence)}
        />

        <DeskSection
          title="Payment"
          className="billing-card"
          data-testid="billing-payment"
          actions={
            onManagePayment === undefined || isFreeTier || isTrialing ? null : (
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => void onManagePayment()}
              >
                Manage payment method
              </button>
            )
          }
        >
          <p className="muted billing-note">
            {isFreeTier
              ? 'The Free plan needs no payment method.'
              : isTrialing
                ? 'No card is on file during the trial. Upgrade to add one.'
                : isCustomContract
                  ? 'Invoiced under your contract. Past invoices are on the Invoices tab.'
                  : 'Card and billing address are kept by our payment provider. Receipts are on the Invoices tab.'}
          </p>
        </DeskSection>
      </div>

      <PlanComparison
        plans={ordered}
        currentPlanId={entitlements.plan_id}
        cadence={cadence}
        isCustomContract={isCustomContract}
        onCadenceChange={setCadence}
        onUpgrade={(plan) => void onUpgrade(plan, cadence)}
        onDowngrade={setPendingDowngrade}
      />

      {pendingDowngrade === null ? null : (
        <ConfirmDialog
          title={`Downgrade to ${pendingDowngrade.display_name}?`}
          tone="danger"
          confirmLabel="Downgrade anyway"
          cancelLabel="Keep current plan"
          error={actionError}
          consequence={
            <>
              The change takes effect immediately. Every play, game and playbook option is preserved
              — you lose access to the features below, not the data behind them.
            </>
          }
          onCancel={() => setPendingDowngrade(null)}
          onConfirm={async () => {
            if (await onChangePlan(pendingDowngrade)) setPendingDowngrade(null);
          }}
        >
          <DowngradeConsequences from={view} to={pendingDowngrade} />
        </ConfirmDialog>
      )}

      {isCancelOpen ? (
        <ConfirmDialog
          title="Cancel your subscription?"
          tone="danger"
          confirmLabel="Cancel subscription"
          cancelLabel="Keep my plan"
          error={actionError}
          consequence={
            <>
              Your {entitlements.plan_display_name} plan stays active until{' '}
              {formatDate(subscription?.current_period_end)}. After that the team moves to the Free
              tier: {FREE_TIER_SEATS} seats, {FREE_TIER_GAMES} games per season and read-only access
              to existing data. No refund is issued for the remaining period, and no further charge
              is made.
            </>
          }
          onCancel={() => setIsCancelOpen(false)}
          onConfirm={async () => {
            if (await onCancel()) setIsCancelOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

/** Feature + cap losses for a specific source → target transition. */
function DowngradeConsequences({
  from,
  to,
}: {
  from: BillingSubscriptionView;
  to: BillingPlan;
}): JSX.Element {
  const features = planFeatureLosses(from.entitlements.features, to.features);
  const limits = limitLosses(from.entitlements.limits, to.limits);
  if (features.length === 0 && limits.length === 0) {
    return <p className="muted">This plan keeps every feature and limit you use today.</p>;
  }
  return (
    <div className="downgrade-consequences">
      {features.length === 0 ? null : (
        <>
          <p className="downgrade-consequences__title">You will lose access to</p>
          <ul>
            {features.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
        </>
      )}
      {limits.length === 0 ? null : (
        <>
          <p className="downgrade-consequences__title">Lower limits</p>
          <ul>
            {limits.map((entry) => (
              <li key={entry}>{entry}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
